import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { comptabiliteApi } from '../../services/comptabiliteApi';
import { formatCurrency } from '../../utils/formatters';
import { downloadFile } from '../../utils/download';

const num = (s: string | number | null | undefined) => Number(s ?? 0);

const GrandLivreView: React.FC = () => {
  const [compte, setCompte] = useState<string>('');

  const { data: comptes = [] } = useQuery({ queryKey: ['compta', 'comptes'], queryFn: () => comptabiliteApi.getComptes() });

  useEffect(() => {
    if (!compte && comptes.length > 0) setCompte(comptes[0].numero);
  }, [comptes, compte]);

  const { data, isLoading } = useQuery({
    queryKey: ['compta', 'grand-livre', compte],
    queryFn: () => comptabiliteApi.getGrandLivre(compte),
    enabled: !!compte,
  });

  return (
    <div className="w-full space-y-3">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Compte</label>
        <select value={compte} onChange={(e) => setCompte(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-gray-700">
          {comptes.map((c) => <option key={c.id} value={c.numero}>{c.numero} — {c.intitule}</option>)}
        </select>
        {compte && (
          <button onClick={() => downloadFile('/comptabilite/rapports/export/', `grand_livre_${compte}.xlsx`, { etat: 'grand_livre', compte })}
            className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm px-3 py-2 rounded-lg ml-auto">
            <Download className="h-4 w-4" /> Excel
          </button>
        )}
      </div>

      {isLoading || !data ? (
        <div className="p-6 text-gray-500">Chargement…</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">{data.compte.numero} — {data.compte.intitule}</h3>
            <p className="text-sm text-gray-500">Solde final : <span className="font-semibold text-gray-900">{formatCurrency(num(data.solde_final))}</span></p>
          </div>
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Pièce</th>
                <th className="px-4 py-2">Libellé</th>
                <th className="px-4 py-2 text-right">Débit</th>
                <th className="px-4 py-2 text-right">Crédit</th>
                <th className="px-4 py-2 text-right">Solde</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.mouvements.map((m, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-500">{m.date}</td>
                  <td className="px-4 py-2 text-sm font-mono text-gray-500">{m.numero}</td>
                  <td className="px-4 py-2 text-sm text-gray-800">{m.libelle}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-700">{num(m.debit) ? formatCurrency(num(m.debit)) : ''}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-700">{num(m.credit) ? formatCurrency(num(m.credit)) : ''}</td>
                  <td className="px-4 py-2 text-sm text-right font-medium text-gray-900">{formatCurrency(num(m.solde))}</td>
                </tr>
              ))}
              {data.mouvements.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Aucun mouvement.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GrandLivreView;
